import { supabase } from "@/infrastructure/supabase/client";
import type { ReviewFlowRow } from "@/infrastructure/supabase/database.types";

/** The flow-content fields a can_write/admin user edits in the drawer (pre-save,
 *  before the editor stamp is attached). */
export type ReviewFlowFields = Partial<
  Pick<
    ReviewFlowRow,
    | "title"
    | "description"
    | "partial"
    | "priority"
    | "reviewer_key"
    | "steps"
    | "asks"
    | "system"
    | "area"
    | "board_codes"
    | "links"
    | "module_page"
    | "module_ref"
  >
>;

/** The persisted patch — a field edit plus the required editor stamp. */
export type ReviewFlowPatch = ReviewFlowFields & { updated_by: string | null };

/** A new hand-authored review flow (block-v "Add item" → fill the drawer fields). */
export type ReviewFlowCreate = { flow_id: string; title: string } & ReviewFlowPatch;

/**
 * review_flows — the editable, DB-resident flow content /feature-review-sync sends.
 * Reads are open to any signed-in user; the update is gated to can_write/admin by
 * RLS (auth_can_write() / auth_is_admin()), so a viewer's patch is refused server-side
 * regardless of the client-side gate.
 */
export const reviewFlowsRepository = {
  async get(flowId: string): Promise<ReviewFlowRow | null> {
    const { data, error } = await supabase
      .from("review_flows")
      .select("*")
      .eq("flow_id", flowId)
      .maybeSingle();
    if (error) throw error;
    return (data as ReviewFlowRow | null) ?? null;
  },

  async update(flowId: string, patch: ReviewFlowPatch): Promise<ReviewFlowRow> {
    const { data, error } = await supabase
      .from("review_flows")
      .update(patch)
      .eq("flow_id", flowId)
      .select("*")
      .single();
    if (error) throw error;
    return data as ReviewFlowRow;
  },

  /** Create the content row for a hand-authored block-v item (RLS: can_write/admin). */
  async create(row: ReviewFlowCreate): Promise<ReviewFlowRow> {
    const { data, error } = await supabase.from("review_flows").insert(row).select("*").single();
    if (error) throw error;
    return data as ReviewFlowRow;
  },
};
